const TRY_ON_WATERMARK_SRC = "/logo_png/flat.png";
const TRY_ON_DOWNLOAD_NAME = "build-a-dress-virtual-try-on.jpg";
const TRY_ON_DOWNLOAD_QUALITY = 0.92;
const TRY_ON_WATERMARK_OPACITY = 0.68;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Unable to load an image for download."));
    image.src = src;
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("Unable to prepare the try-on download."));
      },
      "image/jpeg",
      TRY_ON_DOWNLOAD_QUALITY
    );
  });
}

/**
 * Adds the brand watermark only to the downloaded copy. The preview's source
 * object URL is never changed or re-encoded.
 */
export async function downloadTryOnWithWatermark(
  previewUrl: string
): Promise<void> {
  const [tryOnImage, logoImage] = await Promise.all([
    loadImage(previewUrl),
    loadImage(TRY_ON_WATERMARK_SRC),
  ]);

  const width = tryOnImage.naturalWidth || tryOnImage.width;
  const height = tryOnImage.naturalHeight || tryOnImage.height;
  const logoWidth = logoImage.naturalWidth || logoImage.width;
  const logoHeight = logoImage.naturalHeight || logoImage.height;
  if (!width || !height || !logoWidth || !logoHeight) {
    throw new Error("Unable to prepare the try-on download.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to prepare the try-on download.");
  }

  context.drawImage(tryOnImage, 0, 0, width, height);

  const shorterSide = Math.min(width, height);
  const margin = Math.max(12, Math.round(shorterSide * 0.025));
  const watermarkScale = Math.min(
    (width * 0.18) / logoWidth,
    (height * 0.045) / logoHeight
  );
  const watermarkWidth = Math.max(1, Math.round(logoWidth * watermarkScale));
  const watermarkHeight = Math.max(1, Math.round(logoHeight * watermarkScale));

  context.save();
  context.globalAlpha = TRY_ON_WATERMARK_OPACITY;
  context.shadowColor = "rgba(0, 0, 0, 0.16)";
  context.shadowBlur = Math.max(2, Math.round(shorterSide * 0.003));
  context.drawImage(logoImage, margin, margin, watermarkWidth, watermarkHeight);
  context.restore();

  const downloadBlob = await canvasToJpeg(canvas);
  const downloadUrl = URL.createObjectURL(downloadBlob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = TRY_ON_DOWNLOAD_NAME;
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1_000);
}
